<template>
  <messenger-widget-chat-view
    :view-settings="chatViewSettings"
    :chat-state="chatState"
    :agent-typing-count="agentTypingCount"
    :message-list-items="messageListItems"
    :message-list-state="messageListState"
    :is-satisfaction-survey-shown="isSatisfactionSurveyShown"
    :scroll-bar-id="messengerChatScrollBarId"
    :new-message-divider-id="messengerChatNewMessageDividerId"
    :new-message-state="newMessageState"
    :is-scroll-button-shown="isScrollButtonShown"
    :number-of-new-messages="numberOfNewMessages"
    :max-file-size="messengerMaxFileSize"
    @back="$emit('back')"
    @end-chat="openEndChatPopover"
    @resize="$emit('resize', $event)"
    @submit="submit()"
    @start-new-chat="$emit('start-new-chat')"
    @leave-feedback="leaveFeedback"
    @close-window="$emit('close-window')"
    @new-messages-viewed="setNewMessageStateAsViewed"
    @scroll-button-click="onScrollButtonClick"
    @files-picked="uploadAttachments"
    @remove-attachment="removeAttachment"
    @retry-attachment="retryAttachment"
    @load-next-page="onLoadNextPage"
  >
    <template v-slot:alert>
      <messenger-alert-container :scopes="['chat', 'chatConnection']" />
    </template>
  </messenger-widget-chat-view>
</template>

<script setup lang="ts">
import { useWidgetChatViewSettings } from '@widgets/messenger/compositions/useWidgetChatViewSettings'
import MessengerWidgetChatView from '@widgets/messenger/components/views/MessengerWidgetChatView.vue'
import type { MessengerChatForm } from '@widgets/messenger/types'
import { castCommonErrorToMessages } from '@widgets/messenger/utils/castCommonErrorToMessages'
import { useWidgetChat } from '@widgets/messenger/compositions/useWidgetChat'
import { useWidgetChatPopovers } from '@widgets/messenger/compositions/useWidgetChatPopovers'
import { isChatRatedState } from '@widgets/messenger/types/chat/chat'
import {
  messengerChatNewMessageDividerId,
  messengerChatScrollBarId,
  messengerMaxFileSize,
} from '@widgets/messenger/const'
import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import { useChatScroll } from '@widgets/messenger/compositions/useChatScroll'
import { useWidgetChatTyping } from '@widgets/messenger/compositions/useWidgetChatTyping'
import { useWidgetChatEditorAttachments } from '@widgets/messenger/compositions/useWidgetChatEditorAttachments'
import type { WidgetAttachment } from '@widgets/shared/types'
import MessengerAlertContainer from '@widgets/messenger/containers/MessengerAlertContainer.vue'
import { useWidgetChatPages } from '@widgets/messenger/compositions/useWidgetChatPages'
import { useWidgetForm, type WidgetFieldsConfig } from '@/composition/widgetForm'
import { onBeforeUnmount, onMounted, ref, watch } from '@/composition/vue/compositionApi'

const props = defineProps<{
  formValuesCache: Partial<MessengerChatForm>
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'resize', height: number): void
  (e: 'start-new-chat'): void
  (e: 'close-window'): void
  (e: 'update-form-values-cache', value: Partial<MessengerChatForm>): void
}>()

const chatViewSettings = useWidgetChatViewSettings()
const { messageListState, newMessageState, numberOfNewMessages, setNewMessageStateAsViewed, resetNewMessageState } =
  useChatStore()
const { messageListItems, chatState, agentTypingCount, isSatisfactionSurveyShown, sendMessage } = useWidgetChat()
const { fetchNextPage } = useWidgetChatPages()
const { openEndChatPopover, openSatisfactionSurveyRatePopover, openSatisfactionSurveyCommentPopover } =
  useWidgetChatPopovers()
const { isScrollButtonShown, onScrollButtonClick, mountScroll, unmountScroll, getScrollState, scrollBy } =
  useChatScroll()
const {
  attachments,
  uploadAttachments,
  removeAttachment,
  retryAttachment,
  getUploadedAttachments,
  setAttachments,
  clearAttachments,
} = useWidgetChatEditorAttachments()

let initialAttachments: WidgetAttachment[] = []
if (attachments.value.length) {
  initialAttachments = attachments.value
} else if (props.formValuesCache.attachments?.length) {
  initialAttachments = props.formValuesCache.attachments
}
setAttachments(initialAttachments)

const onLoadNextPage = async () => {
  const prevHeight = getScrollState()?.scrollHeight
  await fetchNextPage()
  setTimeout(() => {
    const newHeight = getScrollState()?.scrollHeight
    if (prevHeight && newHeight) {
      scrollBy(newHeight - prevHeight)
    }
  }, 0)
}

const fieldsConfig = {
  message: {
    value: props.formValuesCache.message ?? '',
  },
  attachments: {
    value: initialAttachments,
  },
} satisfies WidgetFieldsConfig<MessengerChatForm>

const isMessageSending = ref(false)
const { submit, setFieldValue, form } = useWidgetForm<MessengerChatForm>(
  fieldsConfig,
  async (formData) => {
    isMessageSending.value = true
    setFieldValue('message', '')
    clearAttachments()
    try {
      await sendMessage(formData)
    } finally {
      isMessageSending.value = false
    }
  },
  {},
  castCommonErrorToMessages,
)

watch(
  form,
  (value) => {
    emit('update-form-values-cache', {
      message: value.message,
      attachments: getUploadedAttachments(),
    })
  },
  { deep: true, flush: 'sync' },
)

watch(
  attachments,
  (value) => {
    setFieldValue('attachments', value)
  },
  { deep: true, flush: 'sync' },
)

useWidgetChatTyping(() => form.message, isMessageSending)

const leaveFeedback = () => {
  if (isChatRatedState(chatState.value)) {
    openSatisfactionSurveyCommentPopover()
  } else {
    openSatisfactionSurveyRatePopover()
  }
}

onMounted(() => {
  watch(
    messageListItems,
    (value) => {
      if (value.length) {
        setTimeout(() => {
          mountScroll()
        }, 0)
      }
    },
    { once: true, immediate: true },
  )
})

onBeforeUnmount(() => {
  if (newMessageState.value?.isViewed) {
    resetNewMessageState()
  }
  unmountScroll()
})
</script>
